const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const SKIP_SLOT_LABELS = new Set(['total units', 'total', 'units']);

const normalizeLabel = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/\u2013|\u2014/g, '-')
  .replace(/\s+/g, ' ')
  .replace(/\b(hrs|hours)\b/g, '')
  .trim();

const padHour = (value) => String(value).padStart(2, '0');

const hourTokenFromPart = (part) => {
  const match = String(part || '').trim().match(/^(\d{1,2})(?::\d{2})?$/);
  if (!match) return null;
  const hour = Number(match[1]);
  if (!Number.isInteger(hour) || hour < 0 || hour > 24) return null;
  return hour;
};

const timeBlockLabelVariants = (block) => {
  const text = String(block || '').trim();
  if (!text || !text.includes('-')) return new Set();

  const [startPart, endPart] = text.split('-', 2).map((part) => part.trim());
  const startHh = startPart.split(':', 1)[0].replace(/^0+/, '') || '0';
  const endHh = endPart.split(':', 1)[0].replace(/^0+/, '') || '0';
  const startHh2 = padHour(startPart.split(':', 1)[0]);
  const endHh2 = padHour(endPart.split(':', 1)[0]);

  const variants = new Set([
    text,
    text.replace(/-/g, ' to '),
    `${startHh} to ${endHh}`,
    `${startHh2} to ${endHh2}`,
    `${startHh}-${endHh}`,
    `${startHh2}-${endHh2}`,
    `${startHh}:00-${endHh}:00`,
    `${startHh2}:00-${endHh2}:00`,
    `${startHh}:00 to ${endHh}:00`,
    `${startHh2}:00 to ${endHh2}:00`,
  ]);

  // Common billing shorthand where 24 means midnight/end-of-day.
  if (endHh2 === '00') {
    variants.add(`${startHh2} to 24`);
    variants.add(`${startHh} to 24`);
  }
  if (startHh2 === '00') {
    variants.add(`24 to ${endHh2}`);
    variants.add(`24 to ${endHh}`);
  }

  return variants;
};

const slotTimeBlockLabelVariants = (timeBlocks) => {
  const variants = new Set();
  const blockVariantSets = (timeBlocks || []).map((block) => timeBlockLabelVariants(block));
  blockVariantSets.forEach((blockVariants) => {
    blockVariants.forEach((variant) => variants.add(variant));
  });

  if (blockVariantSets.length > 1) {
    const baseBlocks = (timeBlocks || []).filter((block) => String(block || '').trim());
    const toRangeLabel = (block, pad) => {
      const [startPart, endPart] = String(block).split('-', 2);
      const start = pad ? padHour(startPart.split(':', 1)[0]) : startPart.split(':', 1)[0].replace(/^0+/, '') || '0';
      const end = pad ? padHour(endPart.split(':', 1)[0]) : endPart.split(':', 1)[0].replace(/^0+/, '') || '0';
      return `${start} to ${end}`;
    };

    [' & ', ', ', ' and '].forEach((separator) => {
      variants.add(baseBlocks.join(separator));
      variants.add(baseBlocks.map((block) => block.replace(/-/g, ' to ')).join(separator));
      variants.add(baseBlocks.map((block) => toRangeLabel(block, false)).join(separator));
      variants.add(baseBlocks.map((block) => toRangeLabel(block, true)).join(separator));
    });
  }

  return [...variants].filter(Boolean);
};

const parseTimeRangeLabel = (label) => {
  const normalized = normalizeLabel(label);
  const match = normalized.match(/^(\d{1,2}(?::\d{2})?)\s*(?:to|-)\s*(\d{1,2}(?::\d{2})?)$/);
  if (!match) return null;
  const startHour = hourTokenFromPart(match[1]);
  const endHour = hourTokenFromPart(match[2]);
  if (startHour === null || endHour === null) return null;
  return {
    startHour: startHour === 24 ? 0 : startHour,
    endHour: endHour === 24 ? 0 : endHour,
    rawEndWas24: endHour === 24,
    rawStartWas24: startHour === 24,
  };
};

const parseTimeBlock = (block) => {
  const text = String(block || '').trim();
  if (!text.includes('-')) return null;
  const [startPart, endPart] = text.split('-', 2).map((part) => part.trim());
  const startHour = hourTokenFromPart(startPart);
  const endHour = hourTokenFromPart(endPart);
  if (startHour === null || endHour === null) return null;
  return {
    startHour: startHour === 24 ? 0 : startHour,
    endHour: endHour === 24 ? 0 : endHour,
  };
};

const timeRangesEquivalent = (left, right) => {
  if (!left || !right) return false;
  if (left.startHour === right.startHour && left.endHour === right.endHour) return true;
  // "24 to 06" should match "00 to 06"; "17 to 24" should match "17 to 00".
  if (left.rawStartWas24 && left.startHour === right.startHour && left.endHour === right.endHour) return true;
  if (left.rawEndWas24 && left.startHour === right.startHour && left.endHour === right.endHour) return true;
  if (right.rawStartWas24 && left.startHour === right.startHour && left.endHour === right.endHour) return true;
  if (right.rawEndWas24 && left.startHour === right.startHour && left.endHour === right.endHour) return true;
  return false;
};

const buildSlotLabelVariants = (slot, slotDetailsByName) => {
  const variants = new Set();
  variants.add(normalizeLabel(slot));
  const detail = slotDetailsByName[slot] || {};
  if (detail.displayName) {
    variants.add(normalizeLabel(detail.displayName));
  }
  slotTimeBlockLabelVariants(detail.timeBlocks || []).forEach((variant) => {
    variants.add(normalizeLabel(variant));
  });
  return variants;
};

const parseCsvLine = (line) => {
  const cells = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === ',' && !inQuotes) {
      cells.push(current.trim());
      current = '';
      continue;
    }
    current += ch;
  }
  cells.push(current.trim());
  return cells;
};

const monthIndexFromHeader = (header) => {
  const text = String(header || '').trim();
  if (!text) return null;
  const lowered = text.toLowerCase();
  const monthIdx = MONTHS.findIndex((month) => month.toLowerCase() === lowered);
  if (monthIdx >= 0) return monthIdx;
  const asNumber = Number(text);
  if (Number.isInteger(asNumber) && asNumber >= 1 && asNumber <= 12) {
    return asNumber - 1;
  }
  if (/^0[1-9]$|^1[0-2]$/.test(text)) {
    return Number(text) - 1;
  }
  return null;
};

const resolveSlotKey = (rawSlot, slotRows, slotDetailsByName) => {
  const normalized = normalizeLabel(rawSlot);
  if (!normalized || SKIP_SLOT_LABELS.has(normalized)) {
    return null;
  }

  const displayMatches = slotRows.filter((slot) => {
    const display = normalizeLabel(slotDetailsByName[slot]?.displayName || slot);
    return display === normalized;
  });
  if (displayMatches.length === 1) {
    return displayMatches[0];
  }
  if (displayMatches.length > 1) {
    throw new Error(
      `Slot "${rawSlot}" matches multiple policy slots. Use canonical slot names: ${slotRows.join(', ')}`
    );
  }

  const canonicalMatch = slotRows.find((slot) => (
    normalizeLabel(slot) === normalized || String(slot).trim().toLowerCase() === String(rawSlot).trim().toLowerCase()
  ));
  if (canonicalMatch) return canonicalMatch;

  const labelVariantMatches = slotRows.filter((slot) => buildSlotLabelVariants(slot, slotDetailsByName).has(normalized));
  if (labelVariantMatches.length === 1) {
    return labelVariantMatches[0];
  }
  if (labelVariantMatches.length > 1) {
    throw new Error(
      `Slot "${rawSlot}" matches multiple policy slots. Use canonical slot names: ${slotRows.join(', ')}`
    );
  }

  const parsedRange = parseTimeRangeLabel(rawSlot);
  if (parsedRange) {
    const rangeMatches = slotRows.filter((slot) => {
      const timeBlocks = slotDetailsByName[slot]?.timeBlocks || [];
      if (timeBlocks.length !== 1) return false;
      const blockRange = parseTimeBlock(timeBlocks[0]);
      return timeRangesEquivalent(parsedRange, blockRange);
    });
    if (rangeMatches.length === 1) {
      return rangeMatches[0];
    }
    if (rangeMatches.length > 1) {
      throw new Error(
        `Slot "${rawSlot}" matches multiple policy slots. Use canonical slot names: ${slotRows.join(', ')}`
      );
    }
  }

  throw new Error(
    `Unsupported slot "${rawSlot}". Expected rows for: ${slotRows.join(', ')}`
  );
};

/**
 * Parse a compact slot CSV (Slot + Jan-Dec columns) into the consumption grid shape.
 */
export const parseCompactSlotCsvText = (csvText, slotRows, slotDetailsByName = {}) => {
  const lines = String(csvText || '')
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) {
    throw new Error('CSV file is empty');
  }

  const headers = parseCsvLine(lines[0]);
  const slotColIdx = headers.findIndex((header) => {
    const key = normalizeLabel(header);
    return key === 'slot' || key === 'time slot' || key === 'timeslot';
  });
  const slotIndex = slotColIdx >= 0 ? slotColIdx : 0;

  const monthColumns = headers
    .map((header, idx) => ({ header, idx, monthIdx: monthIndexFromHeader(header) }))
    .filter((entry) => entry.monthIdx !== null && entry.idx !== slotIndex);

  if (!monthColumns.length) {
    throw new Error('CSV must include Jan-Dec month columns');
  }

  const grid = slotRows.reduce((acc, slot) => {
    acc[slot] = MONTHS.reduce((row, month) => {
      row[month] = '';
      return row;
    }, {});
    return acc;
  }, {});

  for (let lineIdx = 1; lineIdx < lines.length; lineIdx += 1) {
    const cells = parseCsvLine(lines[lineIdx]);
    if (!cells.length) continue;
    const rawSlot = cells[slotIndex];
    const slotKey = resolveSlotKey(rawSlot, slotRows, slotDetailsByName);
    if (!slotKey) continue;

    monthColumns.forEach(({ idx, monthIdx }) => {
      const rawValue = cells[idx];
      if (rawValue === undefined || String(rawValue).trim() === '') return;
      const numeric = Number(String(rawValue).replace(/,/g, ''));
      if (!Number.isFinite(numeric)) {
        throw new Error(`Invalid value "${rawValue}" for ${rawSlot}, ${MONTHS[monthIdx]}`);
      }
      if (numeric < 0) {
        throw new Error(`Negative value "${rawValue}" for ${rawSlot}, ${MONTHS[monthIdx]}`);
      }
      grid[slotKey][MONTHS[monthIdx]] = String(numeric);
    });
  }

  return grid;
};

export const parseCompactSlotCsvFile = (file, slotRows, slotDetailsByName = {}) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    try {
      resolve(parseCompactSlotCsvText(reader.result, slotRows, slotDetailsByName));
    } catch (error) {
      reject(error);
    }
  };
  reader.onerror = () => reject(new Error('Failed to read CSV file'));
  reader.readAsText(file);
});
